import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import PageHero from '@/components/feature/PageHero';
import CtaSection from '@/components/feature/CtaSection';
import Reveal from '@/components/base/Reveal';
import { services } from '@/mocks/services';
import { company } from '@/mocks/site';

export default function Offers() {
  return (
    <SiteLayout>
      <PageHero
        breadcrumb="Offres"
        label="Nos services industriels"
        title="Dix expertises, un seul interlocuteur"
        description="Chaudronnerie, tuyauterie, soudure, montage, maintenance, peinture, formation et logistique : SGSCTIC couvre l'ensemble du cycle de vie de vos ouvrages industriels."
        image="https://sgsctic.com/wp-content/uploads/2026/01/WhatsApp-Image-2025-12-19-at-10.05.27.jpeg"
        imageAlt="Réseau de tuyauterie industrielle en atelier"
      />

      {/* Quick nav */}
      <section className="border-b border-background-200 bg-background-50 py-8">
        <div className="container-wide">
          <div className="flex flex-wrap gap-2">
            {services.map((service) => (
              <a
                key={service.slug}
                href={`#${service.slug}`}
                className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2 text-[11px] uppercase tracking-[0.12em] text-foreground-700 transition-colors duration-300 hover:border-primary-500 hover:bg-primary-500 hover:text-background-50"
              >
                <span className="font-label text-[10px] text-primary-600">{service.index}</span>
                {service.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-16 md:py-24">
        <div className="container-wide space-y-16 md:space-y-24">
          {services.map((service, index) => {
            const reversed = index % 2 === 1;
            return (
              <div key={service.slug} id={service.slug} className="scroll-mt-28">
                <div
                  className={`grid items-center gap-10 lg:grid-cols-2 lg:gap-16 ${
                    reversed ? 'lg:[&>*:first-child]:order-2' : ''
                  }`}
                >
                  <Reveal variant="fade" className="relative">
                    <div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg bg-background-200">
                      <img
                        src={service.image}
                        alt={service.title}
                        title={`${service.title} — SGSCTIC Douala`}
                        className="h-full w-full object-cover object-top transition-transform duration-[900ms] ease-smooth hover:scale-[1.04]"
                      />
                      <span className="absolute left-4 top-4 flex h-11 w-11 items-center justify-center bg-foreground-950/90 font-heading text-lg font-semibold text-primary-400">
                        {service.index}
                      </span>
                    </div>
                  </Reveal>

                  <Reveal delay={100}>
                    <div className="flex items-center gap-3">
                      <span className="h-px w-8 bg-primary-500" />
                      <span className="label-mono text-primary-600">Expertise {service.index}</span>
                    </div>
                    <h2 className="display-title mt-4 text-3xl text-foreground-950 md:text-4xl lg:text-5xl">
                      {service.title}
                    </h2>
                    <p className="mt-4 text-sm font-medium leading-relaxed text-foreground-800">
                      {service.short}
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                      {service.long}
                    </p>

                    <ul className="mt-7 grid gap-3 sm:grid-cols-2">
                      {service.capabilities.map((capability) => (
                        <li key={capability} className="flex items-start gap-3">
                          <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary-500/10 text-primary-600">
                            <i className="ri-check-line text-xs" />
                          </span>
                          <span className="text-xs leading-relaxed text-foreground-700">
                            {capability}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-8 flex flex-wrap items-center gap-5">
                      <Link
                        to="/contact"
                        className="group inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.12em] text-background-50 transition-colors duration-300 hover:bg-primary-600"
                      >
                        Demander une étude
                        <i className="ri-arrow-right-line text-sm transition-transform duration-300 group-hover:translate-x-1" />
                      </Link>
                      <a
                        href={company.phoneHref}
                        className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold uppercase tracking-[0.12em] text-foreground-700 transition-colors hover:text-primary-600"
                      >
                        <i className="ri-phone-line text-primary-500" />
                        {company.phone}
                      </a>
                    </div>
                  </Reveal>
                </div>

                {index < services.length - 1 ? (
                  <div className="mt-16 h-px w-full bg-background-200 md:mt-24" />
                ) : null}
              </div>
            );
          })}
        </div>
      </section>

      {/* Custom project note */}
      <section className="bg-background-100 py-16 md:py-20">
        <div className="container-wide">
          <Reveal>
            <div className="grid gap-8 rounded-lg border border-background-200 bg-background-50 p-8 md:grid-cols-[1.2fr_1fr] md:items-center md:p-12">
              <div>
                <span className="label-mono text-primary-600">Projet spécifique</span>
                <h2 className="display-title mt-4 text-3xl text-foreground-950 md:text-4xl">
                  Votre besoin ne rentre
                  <br />
                  dans aucune case ?
                </h2>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground-600">
                  Un ouvrage hors standard, une intervention en arrêt de production, un chantier
                  multi-lots : nous construisons la réponse technique avec vous, à partir de vos
                  plans ou d'un simple relevé sur site.
                </p>
              </div>
              <div className="flex flex-col gap-3 md:items-end">
                <Link
                  to="/contact"
                  className="btn-shine flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-4 text-sm font-semibold uppercase tracking-[0.1em] text-background-50 transition-colors hover:bg-primary-600 md:w-auto"
                >
                  Décrire mon projet
                  <i className="ri-arrow-right-up-line text-base" />
                </Link>
                <Link
                  to="/a-propos"
                  className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-950/20 px-6 py-4 text-sm font-semibold uppercase tracking-[0.1em] text-foreground-950 transition-colors hover:border-foreground-950 md:w-auto"
                >
                  Découvrir l'entreprise
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaSection
        title="Prêt à lancer"
        accent="votre prochain chantier ?"
        description="Un devis clair, des délais tenus, une équipe joignable. Dites-nous ce que vous devez construire, réparer ou maintenir."
      />
    </SiteLayout>
  );
}